import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import DashboardLayout from '../../components/DashboardLayout';
import QRBarcodeScanner from '../../components/QRBarcodeScanner';
import UploadBarcodeView from '../../components/UploadBarcodeView';
import SuccessModal from '../../components/modals/SuccessModal';
import { validateSerialNumber, parseSerialNumber } from '../../config/deviceModels';
import { parseQRCodeText, displayParsedQRData } from '../../utils/decodeQRCode';
import '../../styles/pages/add-device.css';
import { useAuth } from '../../hooks/useAuth';
import { Upload, X } from 'lucide-react';

const AddDevicePage: React.FC = () => {
  const navigate = useNavigate();
  const [showScanner, setShowScanner] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Manual serial-number entry modal
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualSerial, setManualSerial] = useState('');
  const [manualSerialError, setManualSerialError] = useState('');

  // Detect if user is on mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleBackClick = () => {
    navigate('/devices');
  };

  const handleStartScanning = () => {
    setShowScanner(true);
  };

  const handleScanSuccess = (decodedText: string): boolean => {
    try {
      // Parse the decoded text using the utility function
      const parsedData = parseQRCodeText(decodedText);

      // Display parsed data in console
      displayParsedQRData(parsedData);

    const trimmedSerial = parsedData.serialNumber?.trim() || '';

    // Verify Serial Number extraction before navigation
    if (!parsedData.serialNumber || parsedData.serialNumber.trim() === '') {
      // Invalid QR code - stop scanner and show error
      setErrorMessage('Failed to extract Serial Number from QR code. Please try again.');
      setShowErrorModal(true);
      setShowScanner(false); // Close scanner
      return false; // Return false to indicate invalid QR code
    }

    // Parse serial number to extract device ID for name generation
    const parsedSerial = parseSerialNumber(trimmedSerial);
    let suggestedDeviceName: string | undefined;
    
    if (parsedSerial && parsedSerial.deviceId) {
      // Device name will be auto-generated as: DeviceType + DeviceID
      // Example: "CORE 0001", "MINI 0002", "Advance 0003"
      // The actual device type description will be filled in ManuallyAddDevicePage
      // when it has access to the API response
      suggestedDeviceName = undefined; // Let ManuallyAddDevicePage generate it
    } else if (parsedData.deviceNumber) {
      // Fallback for legacy format
      suggestedDeviceName = `Device ${parsedData.deviceNumber}`;
    }

      // Navigate to manual add page with device type and serial number pre-filled.
      // We pass data through BOTH URL query params (robust across refresh / StrictMode /
      // mobile-emulator edge cases) AND location.state (used if present). The receiving
      // page merges the two, preferring state when available.
      const navState = {
        preFilledSerialNumber: trimmedSerial,
        preFilledDeviceType: parsedData.deviceType,
        preFilledDeviceTypeId: parsedData.deviceTypeId ?? undefined,
        preFilledDeviceNumber: parsedData.deviceNumber || undefined,
        preFilledDeviceName: suggestedDeviceName,
      };
      const params = new URLSearchParams();
      params.set('sn', trimmedSerial);
      if (parsedData.deviceType) params.set('dt', parsedData.deviceType);
      if (parsedData.deviceNumber) params.set('dn', parsedData.deviceNumber);
      if (parsedData.deviceTypeId != null) params.set('dtId', String(parsedData.deviceTypeId));
      if (suggestedDeviceName) params.set('name', suggestedDeviceName);
      const target = `/manually-add-device?${params.toString()}`;
      navigate(target, { state: navState });
      return true; // Return true to indicate valid QR code
    } catch (error) {
      // Handle parsing errors (e.g., URL-based QR codes, invalid formats)
      // Invalid QR code - stop scanner and show error
      const errorMsg = error instanceof Error ? error.message : 'Invalid QR code format';
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setShowScanner(false); // Close scanner
      setIsProcessing(false);
      return false; // Return false to indicate invalid QR code
    }
  };

  const handleScanError = (error: string) => {
    // Show error modal instead of browser alert
    setErrorMessage(error);
    setShowErrorModal(true);
    setShowScanner(false);
  };

  const handleCloseScanner = () => {
    setShowScanner(false);
  };

  // Bulk Import functionality - Commented out as not required
  const handleBulkImport = () => {
    navigate('/bulk-import-devices');
  };

  // Manual Add functionality - Removed as per requirement (devices can only be added via mobile)
  // const handleManualAdd = () => {
  //   navigate('/manually-add-device');
  // };

  const handleUploadImage = () => {
    // Trigger the hidden file input
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file (JPG, PNG, etc.)');
      setShowErrorModal(true);
      return;
    }

    setIsProcessing(true);

    try {
      // Add a slight delay to show the upload view
      await new Promise(resolve => setTimeout(resolve, 500));

      // Decode QR code/barcode from image using html5-qrcode
      // Library automatically supports QR codes and multiple barcode formats
      const html5QrCode = new Html5Qrcode("temp-qr-reader", { 
        verbose: false  // Disable verbose to reduce console spam
      });
      
      // scanFile supports both QR codes and barcodes
      const decodedText = await (html5QrCode as any).scanFile(file, true);

      // Clean up
      try {
        await html5QrCode.clear();
      } catch (e) {
        // Ignore cleanup errors
      }

      // Validate decoded text
      if (!decodedText || decodedText.trim() === '') {
        throw new Error('No data found in image. Please ensure the QR code is clear and visible.');
      }

      // Add a delay before navigating to show completed progress
      await new Promise(resolve => setTimeout(resolve, 500));

      // Parse and navigate with decoded data
      handleScanSuccess(decodedText);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to decode QR code';
      // Check if it's the "No MultiFormat Readers" error
      const fullErrorMsg = errorMsg.includes('MultiFormat') || errorMsg.includes('No.*QR') || errorMsg.includes('No QR')
        ? 'No QR code was detected. Please ensure the image contains a clear, valid QR code.'
        : `${errorMsg}. Please ensure the image contains a clear, valid QR code.`;
      setErrorMessage(fullErrorMsg);
      setShowErrorModal(true);
      setIsProcessing(false);
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCancelUpload = () => {
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenManualEntry = () => {
    setManualSerial('');
    setManualSerialError('');
    setShowManualEntry(true);
  };

  const handleCloseManualEntry = () => {
    setShowManualEntry(false);
    setManualSerial('');
    setManualSerialError('');
  };

  const handleManualSubmit = () => {
    const trimmed = manualSerial.trim();
    // Validate inline first — cheaper UX than opening the scan-error modal and forcing the
    // user to dismiss it before retyping. The validator returns a specific error per field
    // (prefix, model letter, country code, year) so the user knows exactly what to fix.
    const result = validateSerialNumber(trimmed);
    if (!result.valid) {
      setManualSerialError(result.error || 'Invalid serial number.');
      return;
    }
    setManualSerialError('');
    setShowManualEntry(false);
    // Reuse the full scan pipeline — same parser, same navigation/query params.
    handleScanSuccess(trimmed);
    setManualSerial('');
  };

  return (
    <DashboardLayout>
      <div className="add-device-container zoomOutLevel">
        {/* Header */}
        <div className="add-device-header">
          <div className="add-device-header-title">
            <button
              onClick={handleBackClick}
              className="back-button zoomOutLevelForDeviceInfoCard"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h3 className="device-title">Add New Device</h3>
          </div>
          <p className="device-subtitle mobile-subtitle md:ml-14">
          Add a new SmartEco device to your dashboard.
          </p>
        </div>

        <div className="add-device-main-content">
          {/* Web Flow: Show QR Code Download Message */}
          {!isMobile && (
            <div className="qr-download-section" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem 2rem',
              textAlign: 'center'
               
            }}>
              <div>
                <h2 className="add-device-heading" style={{ marginTop: 0, marginBottom: '1rem' }}>
                  Download SmartEco Mobile App
                </h2>
                <p style={{ 
                  fontSize: '1.1rem', 
                  color: 'var(--color-sub-title-text)',
                  maxWidth: '600px',
                  margin: '0 auto 0.5rem',
                  opacity: '0.8',
                  lineHeight: '1.4'
                }}>
                  To add a new device, please download the SmartEco mobile application. 
                  Scan the QR code below with your mobile device to get started.
                </p>
              </div>

              <div style={{
                padding: '1rem'
              }}>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/SmartEcoAppQRCode.png`}
                  alt="Download SmartEco App"
                  crossOrigin="anonymous"
                  onError={(e) => {
                    console.error('Failed to load QR code image. Attempted path:', (e.target as HTMLImageElement).src);
                    console.error('PUBLIC_URL:', process.env.PUBLIC_URL);
                    console.error('Current origin:', window.location.origin);
                  }}
                  onLoad={() => {
                    console.log('✓ QR code image loaded successfully');
                  }}
                  style={{
                    width: '280px',
                    height: '280px',
                    display: 'block',
                    objectFit: 'contain',
                    margin: '0 auto'
                  }}
                />
              </div>

              {/* Manual serial-number entry — also exposed on web/tablet so users
                  who have a serial on hand can onboard without the mobile app. */}
              <div className="manual-entry-web-cta">
                <span className="manual-entry-web-cta-divider">or</span>
                <button
                  type="button"
                  className="manual-entry-link"
                  onClick={handleOpenManualEntry}
                >
                  Enter Serial Number Manually
                </button>
                <span className="manual-entry-web-cta-divider">or</span>
                <button
                  type="button"
                  className="manual-entry-link"
                  onClick={handleUploadImage}
                  disabled={isProcessing}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <Upload size={16} />
                  Upload QR Image
                </button>
              </div>
              {/* Hidden file input for QR image upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* Mobile Flow: Show Device Scanner Only */}
          {isMobile && (
            <div className="device-scanner-section">
              <h2 className="add-device-heading mt-0">Device Scanner</h2>
              <p className="device-description">
                Scan the QR to auto-fill device details.
              </p>

              <div className="scanner-viewfinder">
                <div className="viewfinder-border">
                  <div className="camera-icon-container">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="camera-icon"
                    >
                      <path d="M12 12m-3.2 0a3.2 3.2 0 1 1 6.4 0a3.2 3.2 0 1 1 -6.4 0" />
                      <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                    </svg>
                  </div>
                  
                  <div className="viewfinder-actions">
                    <button
                      type="button"
                      className="viewfinder-action-link"
                      onClick={handleStartScanning}
                      disabled={showScanner || isProcessing}
                    >
                      Scan
                    </button>
                    <span className="viewfinder-action-separator">or</span>
                    <button
                      type="button"
                      className="viewfinder-action-link"
                      onClick={handleUploadImage}
                      disabled={isProcessing}
                    >
                      Upload
                    </button>
                    <span className="viewfinder-action-hint">QR Image</span>
                  </div>
                </div>
              </div>

              {/* Manual serial-number entry — falls back to the same parse/validate/navigate
                  pipeline, so the resulting form is populated identically to a QR scan. */}
              <button
                type="button"
                className="manual-entry-link"
                onClick={handleOpenManualEntry}
                disabled={showScanner || isProcessing}
              >
                Enter Serial Number Manually
              </button>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              
              {/* Temporary element for html5-qrcode */}
              <div id="temp-qr-reader" style={{ display: 'none' }}></div>

              {/* Bulk import option for only commercial userd */}
              {(user?.role == "Super Admin" || user?.role == "Admin") && (
                <div className='bulk-import-button' onClick={handleBulkImport} >
                  <Upload size={16}/>
                  Bulk Import
                </div>
              )}
            </div>
          )}

          {/* Manual Add Device section is now hidden on both web and mobile */}
        </div>

        {/* QR/Barcode Scanner Modal */}
        {showScanner && (
          <QRBarcodeScanner
            onScanSuccess={handleScanSuccess}
            onScanError={handleScanError}
            onClose={handleCloseScanner}
          />
        )}

        {/* Upload Barcode View */}
        {isProcessing && (
          <UploadBarcodeView onCancel={handleCancelUpload} />
        )}

        {/* Error Modal */}
        <SuccessModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="QR Code Scan Error"
          subtitle={errorMessage}
          status="failed"
          actionText="OK"
          onAction={() => setShowErrorModal(false)}
        />

        {/* Manual Entry Modal */}
        {showManualEntry && (
          <div
            className="manual-entry-overlay"
            role="presentation"
          >
            <div
              className="manual-entry-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Enter Serial Number"
              onClick={e => e.stopPropagation()}
            >
              <div className="manual-entry-header">
                <h3 className="manual-entry-title">Enter Serial Number</h3>
                <button
                  type="button"
                  className="manual-entry-close"
                  onClick={handleCloseManualEntry}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="manual-entry-description">
                Enter the serial number printed on your SmartEco device to auto-fill the form.
              </p>
              <input
                type="text"
                autoFocus
                value={manualSerial}
                onChange={e => {
                  setManualSerial(e.target.value.toUpperCase());
                  if (manualSerialError) setManualSerialError('');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleManualSubmit();
                  if (e.key === 'Escape') handleCloseManualEntry();
                }}
                placeholder="e.g. SEI100C784250001"
                className={`manual-entry-input uppercase${manualSerialError ? ' has-error' : ''}`}
                autoComplete="off"
                spellCheck={false}
                inputMode="text"
                aria-invalid={!!manualSerialError}
                aria-describedby={manualSerialError ? 'manual-entry-error' : undefined}
              />
              {manualSerialError && (
                <p id="manual-entry-error" className="manual-entry-error" role="alert">
                  {manualSerialError}
                </p>
              )}
              <div className="manual-entry-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleCloseManualEntry}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handleManualSubmit}
                  disabled={!manualSerial.trim()}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AddDevicePage;
